// src/features/videos/VideoCard.tsx — a "For you" grid card. Links to the detail route.
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, MetaRow } from '@/ui';
import { formatViews } from './meta';
import type { VideoItem, VideoCreator } from '@/core/models/video';
import { VideoThumb } from './VideoThumb';

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
  text-decoration: none;
  background: ${({ theme }) => (theme.cardsGlass ? 'transparent' : theme.color.surface)};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  transition:
    transform 120ms ease,
    border-color 120ms ease;
`;

const ThumbWrap = styled.div`
  position: relative;
  height: 120px;
  border-radius: 0;
`;

const Body = styled.div`
  display: flex;
  gap: 10px;
  padding: 12px;
`;

const Text = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Title = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

export interface VideoCardProps {
  video: VideoItem;
  creator?: VideoCreator;
}

export function VideoCard({ video, creator }: VideoCardProps) {
  return (
    <Card
      to={`/videos/${video.id}`}
      aria-label={(video.progress ?? 0) >= 1 ? `${video.title}, watched` : video.title}
    >
      <ThumbWrap>
        <VideoThumb video={video} />
      </ThumbWrap>
      <Body>
        {creator && <Avatar name={creator.name} avatarKey={creator.av} size={32} />}
        <Text>
          <Title>{video.title}</Title>
          {creator && <Meta>{creator.name}</Meta>}
          <Meta>
            <MetaRow>
              <span>{formatViews(video.views)}</span>
              <span>{video.ageLabel}</span>
            </MetaRow>
          </Meta>
        </Text>
      </Body>
    </Card>
  );
}
